import type { ReactNode, RefObject } from 'react'

/**
 * The labelled controls a form is built from.
 *
 * One module rather than a copy per field, because every copy is a place for a
 * label to lose its `htmlFor` — and a label that is not tied to its input is
 * invisible to a screen reader and does nothing when clicked. That is the kind
 * of defect nobody notices until somebody who needs it cannot sign up.
 *
 * The three share a wrapper for the same reason: the label, the hint and the
 * `aria-describedby` that connects them are one arrangement, and three spellings
 * of it is two that will drift.
 */

interface Labelling {
  readonly id: string
  readonly label: string
  /**
   * `| undefined` spelled out because `exactOptionalPropertyTypes` is on: a
   * caller that passes an explicit `undefined` — which is what an optional
   * prop forwarded from a parent is — would otherwise not be assignable.
   */
  readonly hint?: string | undefined
}

function Labelled({ id, label, hint, children }: Labelling & { readonly children: ReactNode }) {
  return (
    <div className="mt-4 first:mt-0">
      <label className="mb-1.5 block font-semibold text-[12px] text-t2" htmlFor={id}>
        {label}
      </label>
      {children}
      {hint === undefined ? null : (
        <p id={`${id}-hint`} className="mt-1.5 text-[12px] text-t3">
          {hint}
        </p>
      )}
    </div>
  )
}

/** Shared so a select and a textarea cannot end up looking unlike an input. */
const CONTROL =
  'block w-full rounded-field border border-line bg-surface px-3 py-2.5 text-[14px] text-t1 disabled:bg-surface-2 disabled:text-t3'

export interface FieldProps extends Labelling {
  /**
   * The input's type.
   *
   * `number`, `date`, `tel` and `url` are here for the declared field kinds,
   * which get the right keyboard on a phone and nothing more — nothing
   * validates them, so nothing claims to.
   */
  readonly type:
    | 'text'
    | 'email'
    | 'password'
    | 'number'
    | 'date'
    /** A date and a time together, which is what scheduling asks for. */
    | 'datetime-local'
    | 'tel'
    | 'url'
  /** Absent where there is nothing a browser could usefully fill in. */
  readonly autoComplete?: string | undefined
  readonly value: string
  readonly disabled?: boolean | undefined
  /**
   * Lets the field be left blank.
   *
   * Most of these are required and saying so with the attribute is what makes a
   * browser refuse an empty submit. A reply address is genuinely optional —
   * empty means "replies go to the from address" — and marking it required
   * would make that answer unreachable.
   */
  readonly optional?: boolean
  readonly onChange: (value: string) => void
}

export function Field({
  id,
  label,
  type,
  autoComplete,
  value,
  disabled,
  hint,
  optional,
  onChange,
}: FieldProps) {
  return (
    <Labelled id={id} label={label} hint={hint}>
      <input
        id={id}
        name={id}
        type={type}
        value={value}
        autoComplete={autoComplete}
        disabled={disabled}
        required={optional !== true}
        aria-describedby={hint === undefined ? undefined : `${id}-hint`}
        onChange={(event) => onChange(event.target.value)}
        className={CONTROL}
      />
    </Labelled>
  )
}

export interface TextAreaFieldProps extends Labelling {
  readonly value: string
  readonly disabled: boolean
  readonly rows: number
  /**
   * Handed out so `MergeTokens` can insert at the caret.
   *
   * The value still lives in the view model; the ref only exposes where the
   * cursor is, which is a fact about the DOM and nowhere else.
   */
  readonly fieldRef?: RefObject<HTMLTextAreaElement | null>
  /**
   * Sentences rather than markup, so it is set in the interface font.
   *
   * Monospace is the default because every caller until now was editing a
   * message body, where the alignment is the point. A field asking what a
   * campaign is for is prose, and prose set in monospace reads as a code
   * editor — which tells somebody they are doing something more technical than
   * typing a sentence.
   */
  readonly prose?: boolean | undefined
  readonly onChange: (value: string) => void
}

export function TextAreaField({
  id,
  label,
  value,
  disabled,
  rows,
  hint,
  fieldRef,
  prose,
  onChange,
}: TextAreaFieldProps) {
  return (
    <Labelled id={id} label={label} hint={hint}>
      <textarea
        id={id}
        name={id}
        ref={fieldRef}
        value={value}
        rows={rows}
        disabled={disabled}
        required
        aria-describedby={hint === undefined ? undefined : `${id}-hint`}
        onChange={(event) => onChange(event.target.value)}
        className={`${CONTROL} leading-relaxed ${prose === true ? '' : 'font-mono'}`}
      />
    </Labelled>
  )
}

export interface SelectFieldProps extends Labelling {
  readonly value: string
  readonly disabled?: boolean | undefined
  /** `[value, label]`. The first entry is what an untouched form submits. */
  readonly options: readonly (readonly [string, string])[]
  readonly onChange: (value: string) => void
}

export function SelectField({
  id,
  label,
  value,
  disabled,
  options,
  hint,
  onChange,
}: SelectFieldProps) {
  return (
    <Labelled id={id} label={label} hint={hint}>
      <select
        id={id}
        name={id}
        value={value}
        disabled={disabled}
        aria-describedby={hint === undefined ? undefined : `${id}-hint`}
        onChange={(event) => onChange(event.target.value)}
        className={CONTROL}
      >
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
    </Labelled>
  )
}
